import type { components } from "./apiSchema.generated";

/** A response type as the wire actually carries it.
 *
 * A Pydantic field with a default is not in the schema's `required` list, so
 * `openapi-typescript` renders it `x?:` — as though the response might not carry the key. It
 * does: FastAPI serialises every field of a response model unless the route opts out with
 * `response_model_exclude_unset`, and exactly one route in this backend does, for a documented
 * reason (`app/routes/family.py`'s controls PATCH). `Wire<T>` says that out loud instead of
 * making every reader handle an `undefined` the wire cannot produce.
 *
 * The backend holds the other end:
 * `backend/tests/test_unit_response_serialisation_is_complete.py` fails if a second route ever
 * opts out, which is the edit that would make this wrapper a lie.
 *
 * Only for RESPONSE types. A request body's defaulted field really is optional for the caller,
 * and wrapping one would demand fields the server is happy to fill in.
 */
type Wire<T> = Required<T>;
import type { AspectWire, DesignSpace } from "./world/aspect/aspects";

const BASE = import.meta.env.VITE_API_URL || "";
export { BASE as API_BASE };

/** Resolve a backend-relative media path to something an `<img src>` can load.
 *
 * Asset manifests are deliberately stripped to relative paths on the way out
 * (`backend/app/public_url.py::relative_public_url`) so a row written under a
 * stale `BACKEND_PUBLIC_URL` still resolves in whatever environment reads it.
 * That works inside the sandbox iframe, which carries a `<base href>` pointing
 * at the backend — but the main document has no such base, so a relative
 * `/media/...` resolves against the FRONTEND origin and 404s. That is the
 * broken-image tile in My Stuff's "Me & Pets" (reported 2026-07-31); the older
 * thumbnail surfaces each open-coded this same ternary, which is why they
 * escaped it.
 *
 * Absolute URLs and data/blob URIs pass through untouched.
 */
export function mediaUrl(url: string | null | undefined): string {
  if (!url) return "";
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  return `${BASE}${url.startsWith("/") ? "" : "/"}${url}`;
}

// R15.3 (launch-compliance, 2026-06-22): the canonical access-token path
// is now an HttpOnly cookie set by the backend on login/register/trusted-
// device-login. The browser auto-attaches it on every same-site fetch when
// `credentials: "include"` is set. We still keep an in-memory `accessToken`
// + a Bearer header attach so:
//   1. non-browser clients (mobile native, admin scripts) can use Bearer
//   2. mid-flight requests during cookie set/clear have a fallback
//   3. tests that set a token via setAccessToken() still work
// The backend's get_current_user_id reads the cookie first, falls back to
// Bearer. If both are present the cookie wins.
//
// Stale-token cleanup: any pre-migration token sitting in localStorage is
// purged on first load so we don't accidentally use a long-lived value
// from before the cookie migration. The kid sessionStorage path is left
// alone — child tokens have always been short-lived and tab-scoped.

// Reading sessionStorage in a sandboxed (no allow-same-origin) iframe throws a
// SecurityError — and a `typeof sessionStorage !== "undefined"` guard does NOT
// help (the property exists; the ACCESS is what throws). The runtime bundles
// import this module transitively, so an unguarded read here aborts the bundle's
// IIFE before it assigns window.SharedRuntime/window.AnimationRuntime → "init is not a
// function". Read defensively; the sandboxed runtime gets its token via the
// capability bridge (postMessage), never from storage.
function readSessionStorage(key: string): string | null {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

// Same rule as `readSessionStorage` above: the `typeof localStorage !==
// "undefined"` check does NOT protect against sandboxed iframes (property
// exists; the getter throws SecurityError). Wrap the whole access — otherwise
// the runtime bundles' IIFE aborts before assigning window.SharedRuntime /
// window.AnimationRuntime and the sandbox errors with "init is not a function".
try {
  if (typeof localStorage !== "undefined") localStorage.removeItem("access_token");
} catch { /* sandboxed iframe / SSR / test */ }

let accessToken = readSessionStorage("child_session_token") || "";
let isChildToken = readSessionStorage("is_child_token") === "1";

export function setAccessToken(token: string) {
  // Re-applying the live child session's own token (useAuth's boot refresh does) keeps it a
  // child session. Clearing the markers here made the next refresh present the grown-up's
  // refresh cookie, and a kid who reloaded was signed in as the grown-up (2026-10-24).
  if (isChildToken && token && token === accessToken) return;
  accessToken = token;
  isChildToken = false;
  try { sessionStorage.removeItem("child_session_token"); sessionStorage.removeItem("is_child_token"); } catch { /* SSR/test */ }
}
export function getAccessToken() { return accessToken; }

// ---------------------------------------------------------------------------
// Native shell (ipad-native-shell R2)
//
// Under Capacitor the app is served from `capacitor://localhost`, an origin
// unrelated to api.worldby.me. WebKit will not attach the HttpOnly access or
// refresh cookie to a request from that origin no matter what CORS says, so
// the native path authenticates by Bearer only and refreshes with the token in
// the request body. Web is untouched and stays on cookies (R2.7).
//
// Detection deliberately reads the global rather than importing
// `@capacitor/core` — the same trick `billing/platform.ts` uses. This module is
// pulled transitively into the sandboxed runtime bundles, and an import there
// would drag the Capacitor runtime into a frame that has no bridge.
// ---------------------------------------------------------------------------
type CapacitorLike = { isNativePlatform?: () => boolean; getPlatform?: () => string };

export function isNativeShell(): boolean {
  try {
    const cap = (globalThis as unknown as { Capacitor?: CapacitorLike }).Capacitor;
    if (typeof cap?.isNativePlatform === "function") return cap.isNativePlatform();
    const p = cap?.getPlatform?.();
    return p === "ios" || p === "android";
  } catch {
    return false;
  }
}

// The native refresh token. Held in memory here and mirrored into a platform
// store by the injected persister below — Keychain on iOS (R2.4), which is why
// this is a seam and not a direct `localStorage` write.
let nativeRefreshToken = "";
let persistNativeRefreshToken: (token: string) => void | Promise<void> = () => {};

/** Install the platform-backed store for the native refresh token. */
export function setNativeRefreshTokenPersister(
  persist: (token: string) => void | Promise<void>,
  initial?: string,
): void {
  persistNativeRefreshToken = persist;
  if (initial) nativeRefreshToken = initial;
}

/** Resolves once the platform store has the write (sign-out awaits the
 *  Keychain clear); never rejects. */
export function setNativeRefreshToken(token: string): Promise<void> {
  nativeRefreshToken = token;
  try {
    return Promise.resolve(persistNativeRefreshToken(token)).catch(() => {});
  } catch {
    return Promise.resolve(); /* store must never break auth */
  }
}

export function getNativeRefreshToken(): string { return nativeRefreshToken; }

// ---------------------------------------------------------------------------
// Client version header (ipad-native-shell R15.1 / task 15.1)
//
// An installed app runs the JS it shipped with while the backend moves on every
// push, so the shell names itself on every request: `X-WBM-Client:
// ios/<version>+<build>`. The backend stamps it onto observability and
// usage_events (R15.4) and answers `/meta/client-policy` with it. Set once at
// boot from `PlatformAdapter.getAppVersion()`; web never sets it and sends
// nothing. Descriptive only — the server never authorizes on it.
// ---------------------------------------------------------------------------
let clientHeader = "";

/** Install the app version read from the native shell. No-op off-native. */
export function setClientVersion(v: { version: string; build: string } | null): void {
  if (!v || !isNativeShell()) { clientHeader = ""; return; }
  let platform = "ios";
  try {
    const p = (globalThis as unknown as { Capacitor?: CapacitorLike }).Capacitor?.getPlatform?.();
    if (p === "android") platform = "android";
  } catch { /* hostile global — keep the default */ }
  clientHeader = `${platform}/${v.version}${v.build ? `+${v.build}` : ""}`;
}

/** The installed version this shell reports, or "" on web / before boot read it. */
export function getClientVersion(): string {
  const m = /^[a-z]+\/([^+]+)/.exec(clientHeader);
  return m ? m[1] : "";
}

/** `{ "X-WBM-Client": ... }` on native once the version is known, else `{}`.
 *  Spread into the headers of any fetch that reaches the API. */
export function clientHeaders(): Record<string, string> {
  return clientHeader ? { "X-WBM-Client": clientHeader } : {};
}

// The native device-trust token (ipad-native-shell defect 7). Web keeps it in
// the HttpOnly `wbm_device` cookie; the app is never sent that cookie, so
// `/auth/device/trust` hands a native caller the same server-minted token and
// it rides `X-WBM-Device` on the `/auth/device/*` calls. Persisted through an
// injected store (Keychain, shell lane) like the refresh token. Survives sign
// out on purpose — trust is the device's, not the session's.
let nativeDeviceToken = "";
let persistNativeDeviceToken: (token: string) => void = () => {};

/** Install the platform-backed store for the native device-trust token. */
export function setNativeDeviceTokenPersister(
  persist: (token: string) => void,
  initial?: string,
): void {
  persistNativeDeviceToken = persist;
  if (initial) nativeDeviceToken = initial;
}

function setNativeDeviceToken(token: string): void {
  nativeDeviceToken = token;
  try { persistNativeDeviceToken(token); } catch { /* store must never break auth */ }
}

export function getNativeDeviceToken(): string { return nativeDeviceToken; }

/** `{ "X-WBM-Device": … }` on native when this device is trusted, else `{}`. */
function deviceHeaders(): Record<string, string> {
  return isNativeShell() && nativeDeviceToken ? { "X-WBM-Device": nativeDeviceToken } : {};
}

/** Body fields that ask `/auth/login` and `/auth/register` to return the
 *  refresh token in JSON. Native only — on web the cookie carries it, and a
 *  browser must never see it in readable JSON. Spread into the request body. */
export function nativeAuthFields(): { native_client?: true } {
  return isNativeShell() ? { native_client: true } : {};
}

/** Keep the refresh token a native login / register handed back. Without it
 *  there is nothing to present at the first refresh, and every relaunch (or
 *  the 30-minute access expiry) becomes a sign-out. No-op on web. */
export function adoptNativeRefreshToken(data: { refresh_token?: string | null }): void {
  if (isNativeShell() && data.refresh_token) setNativeRefreshToken(data.refresh_token);
}

/**
 * Tell the server this session is over. Web clears the refresh cookie; native
 * presents its refresh token so the grant is revoked, then forgets it locally
 * — clearing only the in-memory access token would let the next launch
 * restore the session the child just left.
 */
export async function signOutRequest(): Promise<Response> {
  const native = isNativeShell();
  const presented = nativeRefreshToken;
  const keychainCleared = native ? setNativeRefreshToken("") : Promise.resolve();
  // keepalive: WebKit cancels an in-flight fetch when the page navigates, and
  // sign-out navigates — the cancelled POST left the refresh cookie alive.
  const once = () => fetch(`${BASE}/auth/logout`, {
    method: "POST",
    keepalive: true,
    credentials: credentialsMode(),
    headers: {
      ...clientHeaders(),
      ...(native && presented ? { "Content-Type": "application/json" } : {}),
    },
    ...(native && presented
      ? { body: JSON.stringify({ refresh_token: presented }) }
      : {}),
  });
  // Retry once: a dropped logout leaves the cookie (web) or the grant
  // (native) alive on the server.
  try {
    const r = await once();
    return r.ok ? r : await once();
  } catch {
    return once();
  } finally {
    await keychainCleared;
  }
}

/** How long sign-out waits for the server before navigating anyway. */
export const SIGN_OUT_WAIT_MS = 2000;

/**
 * Sign-out's server half, awaited but bounded: navigate only once the logout
 * POST (and the native Keychain clear) is done, or after ``waitMs``. Without
 * the wait WebKit cancelled the POST on every phone and iPad run, so a
 * grown-up's session outlived Log out on a shared device. Never rejects.
 */
export function signOutAndWait(waitMs: number = SIGN_OUT_WAIT_MS): Promise<void> {
  return Promise.race([
    signOutRequest().then(() => {}, () => {}),
    new Promise<void>((resolve) => setTimeout(resolve, waitMs)),
  ]);
}

/**
 * A rejected api call. `.message` is the human string, `.code` the stable
 * machine code when the route returned a structured detail, and `.status` the
 * HTTP status — which is what a caller needs to say something useful about a
 * failure that carries no code at all (5xx, 429, a bare 403).
 */
export type ApiError = Error & { code?: string; detail?: unknown; status?: number };

// FastAPI returns either `{detail: "string"}` or `{detail: {code, message, ...}}`.
// Build an Error that preserves both: `.message` is the human string (so
// existing `err.message.includes(...)` checks keep working), and the
// structured object — when present — is attached as `.detail` and `.code`
// so callers can branch on a stable machine code without string-matching.
function buildApiError(body: unknown, fallback: string, status?: number): ApiError {
  const detail = (body as { detail?: unknown } | null)?.detail ?? null;
  let message: string;
  if (typeof detail === "string") {
    message = detail;
  } else if (detail && typeof detail === "object" && typeof (detail as { message?: unknown }).message === "string") {
    message = (detail as { message: string }).message;
  } else {
    message = fallback;
  }
  const err = new Error(message) as ApiError;
  if (status !== undefined) err.status = status;
  if (detail && typeof detail === "object") {
    err.detail = detail;
    const code = (detail as { code?: unknown }).code;
    if (typeof code === "string") err.code = code;
  }
  return err;
}

// Surfaces (VoiceControls, useNavigationVoice, friends/social routes) all
// hit FastAPI codes in the `consent_required*` family when a kid tenant
// has `subscriptions.consent != 'granted'`. Rather than wire the modal
// open into every callsite, dispatch a window event here so a single
// host (ConsentGateHost, mounted once in AppShell) can react.
function maybeDispatchConsentRequired(err: Error & { code?: string }): void {
  if (!err.code) return;
  if (err.code !== "consent_required_for_session" && err.code !== "consent_required") return;
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent("voice_consent_required", { detail: { code: err.code } }));
  } catch {
    /* event dispatch must never break the error path */
  }
}

/**
 * Seat unit (2026-10-22): an unseated member's voice start or new project is
 * answered 403 `seat_not_held`. One host (BillingChrome) shows the kid-friendly
 * "ask a grown-up" card for every such call, like the consent event above.
 */
export const SEAT_NOT_HELD_EVENT = "wbm:seat-not-held";

function maybeDispatchSeatNotHeld(err: Error & { code?: string }): void {
  if (err.code !== "seat_not_held" || typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(SEAT_NOT_HELD_EVENT));
  } catch {
    /* event dispatch must never break the error path */
  }
}

/**
 * Voice plan gate (2026-10-23): a free or lapsed family's voice start (and a
 * co-edit join on a project whose owner has no plan that creates) is answered
 * 403 `creation_locked`. BillingChrome opens the paywall for a grown-up and the
 * "ask a grown-up" card for a child — never a price for a child.
 */
export const CREATION_LOCKED_EVENT = "wbm:creation-locked";

function maybeDispatchCreationLocked(err: Error & { code?: string }): void {
  if (err.code !== "creation_locked" || typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(CREATION_LOCKED_EVENT));
  } catch {
    /* event dispatch must never break the error path */
  }
}

/**
 * The credentials mode every API call uses.
 *
 * `include` not `same-origin` so the HttpOnly access cookie travels even
 * when the SPA and API live on different subdomains (e.g. app.worldby.me
 * → api.worldby.me). Backend CORS must echo Allow-Credentials: true and
 * a concrete Allow-Origin for this to work cross-origin.
 *
 * Native uses `omit`: no cookie will be attached from `capacitor://localhost`
 * anyway, and asking for credentials on a cross-scheme request only widens
 * what the preflight has to allow. Bearer carries the whole session there.
 *
 * Code outside this file must not write `credentials: "include"` itself — a
 * hand-rolled fetch sends no Bearer, so it works on web (the cookie carries
 * it) and silently 401s on iPad. `no_raw_credentials_include.test.ts`
 * enforces that.
 */
export function credentialsMode(): RequestCredentials {
  return isNativeShell() ? "omit" : "include";
}

/**
 * An authenticated fetch that hands back the raw `Response`.
 *
 * For callers that need the status or body of a failure (a 409 carrying the
 * current spec version, a download) and so cannot use `request`. Same auth as
 * `request`: Bearer when a token is held, the platform's credentials mode, and
 * one silent refresh-and-replay on a 401. `path` is backend-relative.
 */
export async function authFetch(path: string, opts: RequestInit = {}): Promise<Response> {
  const headers: Record<string, string> = { ...clientHeaders(), ...opts.headers as Record<string, string> };
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
  const credentials = credentialsMode();
  const res = await fetch(`${BASE}${path}`, { ...opts, headers, credentials });
  if (res.status === 401 && accessToken && !path.startsWith("/auth/")) {
    // Token likely expired — attempt silent refresh
    const refreshed = await attemptTokenRefresh();
    if (refreshed) {
      headers["Authorization"] = `Bearer ${accessToken}`;
      return fetch(`${BASE}${path}`, { ...opts, headers, credentials });
    }
  }
  return res;
}

export async function request<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await authFetch(path, {
    ...opts,
    headers: { "Content-Type": "application/json", ...opts.headers as Record<string, string> },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = buildApiError(body, `HTTP ${res.status}`, res.status);
    maybeDispatchConsentRequired(err);
    maybeDispatchSeatNotHeld(err);
    maybeDispatchCreationLocked(err);
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

/** Waits between native boot-refresh attempts while the API is unreachable. */
export const NATIVE_BOOT_REFRESH_RETRIES_MS = [1_000, 2_000, 4_000];

/** Refresh the access token now (cookie on web, body token on native). Never
 *  throws; `false` means it could not. For callers outside the request path,
 *  e.g. a WebSocket refused for an expired token (useCanvasWebSocket). */
export async function refreshAccessToken(): Promise<boolean> {
  return attemptTokenRefresh();
}

// Single-flight. Refresh ROTATES the refresh token, so two concurrent refreshes
// present the same token and the second is refused as already-revoked — which
// signs the device out. Concurrent 401s (and the canvas socket's own refresh)
// therefore share one in-flight attempt.
let refreshInFlight: Promise<RefreshOutcome> | null = null;
/** The web boot refresh (`auth.refresh`), shared by concurrent callers. */
let webBootRefresh: Promise<
  components["schemas"]["app__routes__auth__TokenResponse"]
> | null = null;

/**
 * `rejected` — the server answered and refused the token: sign out.
 * `unreachable` — no answer, or a 5xx / 429 (the API restarting mid-deploy, a
 * kid's iPad that woke before its Wi-Fi did): the stored session is still
 * good, so callers must not treat this as a sign-out (ipad-native-shell 2.8).
 */
type RefreshOutcome = "ok" | "rejected" | "unreachable";

function refreshOutcome(): Promise<RefreshOutcome> {
  if (!refreshInFlight) {
    refreshInFlight = doTokenRefresh().finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

async function attemptTokenRefresh(): Promise<boolean> {
  return (await refreshOutcome()) === "ok";
}

function failureOf(res: Response): RefreshOutcome {
  return res.status >= 500 || res.status === 429 ? "unreachable" : "rejected";
}

async function doTokenRefresh(): Promise<RefreshOutcome> {
  try {
    if (isChildToken) {
      // Child tokens refresh via /auth/child/refresh with the token in the body
      // The platform's credentials mode, so the browser keeps the cookie the refresh re-sets:
      // the share page signs in by that cookie, not by this tab's token.
      const res = await fetch(`${BASE}/auth/child/refresh`, {
        method: "POST",
        credentials: credentialsMode(),
        headers: { "Content-Type": "application/json", ...clientHeaders() },
        body: JSON.stringify({ token: accessToken }),
      });
      if (!res.ok) {
        // If the refresh was rejected by the password-change watermark,
        // notify the auth layer so it can clear local state and route the
        // user to /signin with a friendly banner. Without this the request
        // throws a generic error and the component is left in a stuck
        // loading state with no idea why.
        await maybeNotifyWatermarkInvalidation(res);
        return failureOf(res);
      }
      const data = await res.json() as { child_session_token: string };
      accessToken = data.child_session_token;
      sessionStorage.setItem("child_session_token", accessToken);
      return "ok";
    }
    // Native has no refresh cookie, so it presents the token in the body and
    // reads the rotated replacement back out of the response. Refresh ROTATES
    // — the presented grant is revoked server-side — so dropping the returned
    // token locks the device out on the next refresh.
    const native = isNativeShell();
    if (native && !nativeRefreshToken) {
      // Nothing to present. Treat as a hard sign-out rather than looping (R2.5).
      return "rejected";
    }
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...clientHeaders() },
      ...(native
        ? { body: JSON.stringify({ refresh_token: nativeRefreshToken }) }
        : { credentials: "include" as RequestCredentials }),
    });
    if (!res.ok) {
      await maybeNotifyWatermarkInvalidation(res);
      return failureOf(res);
    }
    const data = await res.json() as { access_token: string; refresh_token?: string };
    accessToken = data.access_token;
    if (native && data.refresh_token) setNativeRefreshToken(data.refresh_token);
    return "ok";
  } catch {
    // fetch threw: no network, DNS, TLS, an aborted request. Not a verdict on
    // the token.
    return "unreachable";
  }
}

/**
 * If the refresh failed because the token was invalidated by a password
 * change (watermark), dispatch a custom event so the auth provider can
 * clear local credentials and redirect to /signin. Other refresh
 * failures (network, expired refresh) fall through to the normal
 * "your session expired" path.
 */
async function maybeNotifyWatermarkInvalidation(res: Response): Promise<void> {
  if (res.status !== 401) return;
  try {
    const body = await res.clone().json().catch(() => ({}));
    const detail = (body as { detail?: string }).detail ?? "";
    if (detail.toLowerCase().includes("password change")) {
      // Best-effort dispatch — happens in browser only.
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("auth:invalidated", {
            detail: { reason: "password_changed_elsewhere" },
          }),
        );
      }
    }
  } catch {
    // Ignore — body wasn't JSON; not a watermark error.
  }
}

// ``request`` is exported inline at its declaration above. No re-export
// is needed here — feature modules import it directly from "../api".

/** One row of `GET /auth/device/profiles`'s `family`, from the route's own model.
 *  Grown-ups are password-gated; kids (`grown: false`) sign in one-tap. */
export type DeviceProfile = components["schemas"]["DeviceProfile"];
export type DeviceProfilesResult = components["schemas"]["DeviceProfilesResponse"];
export type DeviceProfileLoginResult = components["schemas"]["TrustedDeviceLoginResponse"];

/**
 * Device-trust client — the server-side "Trust this device" record. The opaque
 * token lives only in the httpOnly `wbm_device` cookie; these calls send it
 * automatically because `/auth/*` paths use `credentials: "include"`. The trust
 * survives logout (logout clears only the refresh cookie).
 */
export const device = {
  /** Persist device trust for the just-authenticated account. Call right after
   *  a successful sign-in / sign-up when the Trust toggle is on. */
  trust: async () => {
    const r = await request<components["schemas"]["DeviceTrustResponse"]>("/auth/device/trust", {
      method: "POST",
      headers: deviceHeaders(),
      ...(isNativeShell() ? { body: JSON.stringify({ native_client: true }) } : {}),
    });
    // Only a native caller is ever handed the token (and only while the
    // backend's NATIVE_DEVICE_TRUST_ENABLED is on).
    if (isNativeShell() && r.device_token) setNativeDeviceToken(r.device_token);
    return { trusted: r.trusted };
  },
  /** List the family trusted on this device (drives the device-aware picker). */
  profiles: () =>
    request<DeviceProfilesResult>("/auth/device/profiles", { headers: deviceHeaders() }),
  /** Revoke this device's trust ("Forget this device"). */
  forget: async () => {
    const r = await request<components["schemas"]["ForgetDeviceResponse"]>("/auth/device/forget", {
      method: "POST",
      headers: deviceHeaders(),
    });
    if (isNativeShell()) setNativeDeviceToken("");
    return r;
  },
  /** One-tap child sign-in on a trusted device. Grown-ups get a 403 (they must
   *  use a password), surfaced as a thrown error the caller can ignore. */
  profileLogin: (userId: string) =>
    request<DeviceProfileLoginResult>("/auth/device/profile-login", {
      method: "POST",
      headers: deviceHeaders(),
      body: JSON.stringify({ user_id: userId }),
    }),
};

// Auth
/** `GET /auth/me`, from the route's own model.
 *
 * Nine keys, every one of them sent on every call. It was hand-written here until
 * 2026-10-29 and it was wrong in a way nobody could see: it declared eight and left out
 * `email`, which the Account tab reads through its own inline shape. A hand-typed response
 * is a claim about the server; this one is the server's. */
export type AuthMe = components["schemas"]["AuthMeResponse"];

export const auth = {
  register: async (email: string, password: string) => {
    const r = await request<components["schemas"]["app__routes__auth__TokenResponse"]>("/auth/register", {
      method: "POST", body: JSON.stringify({ email, password, ...nativeAuthFields() }),
    });
    adoptNativeRefreshToken(r);
    return r;
  },
  login: async (email: string, password: string) => {
    const r = await request<components["schemas"]["app__routes__auth__TokenResponse"]>("/auth/login", {
      method: "POST", body: JSON.stringify({ email, password, ...nativeAuthFields() }),
    });
    adoptNativeRefreshToken(r);
    return r;
  },
  /** Restore a session at boot. Native has no cookie, so it goes through the
   *  body-token refresh (which also stores the rotated token); a bare POST
   *  here would always 401 on iPad and every relaunch would sign out. */
  /** The declared return is `{ access_token }` and NOT the route's model on purpose: two of
   *  the three paths here never call `/auth/refresh` at all. The native and kid paths
   *  refresh through `refreshOutcome()` and then synthesise `{ access_token: accessToken }`
   *  from the in-memory token, so widening this to `TokenResponse` would be a claim those
   *  paths cannot keep. The generated type belongs on the CALL, below, not on the adapter
   *  around it. */
  refresh: async (): Promise<{ access_token: string }> => {
    if (isNativeShell()) {
      // ipad-native-shell 2.8: an iPad opened before its Wi-Fi is back must not
      // land on sign-in with a good Keychain token. Retry an unreachable server
      // briefly; past the budget, throw `offline` (not a 401) so the auth
      // provider keeps the session and tries again when the network returns.
      for (let i = 0; ; i++) {
        const outcome = await refreshOutcome();
        if (outcome === "ok") return { access_token: accessToken };
        if (outcome === "rejected") throw buildApiError({}, "HTTP 401", 401);
        if (i >= NATIVE_BOOT_REFRESH_RETRIES_MS.length) {
          const err = buildApiError({}, "offline");
          err.code = "offline";
          throw err;
        }
        await new Promise((r) => setTimeout(r, NATIVE_BOOT_REFRESH_RETRIES_MS[i]));
      }
    }
    // A kid's one-tap session refreshes its own token and never the refresh cookie: the cookie
    // can still belong to the grown-up who trusted this device, and presenting it after a
    // reload signed the kid in as the grown-up (kid-reload repro B, 2026-10-24).
    if (isChildToken) {
      const outcome = await refreshOutcome();
      if (outcome === "ok") return { access_token: accessToken };
      throw buildApiError({}, "HTTP 401", 401);
    }
    // One boot refresh at a time: refresh ROTATES, so a second call sent with the same
    // cookie before the first answers is refused, and that signs the kid out. StrictMode
    // runs the boot effect twice in dev; on a cold backend the two raced and the second
    // lost (lands-evals, the first walk after every server start, 2026-10-19).
    if (!webBootRefresh) {
      webBootRefresh = request<components["schemas"]["app__routes__auth__TokenResponse"]>("/auth/refresh", {
        method: "POST",
      })
        .finally(() => { webBootRefresh = null; });
    }
    return webBootRefresh;
  },
  me: () => request<AuthMe>("/auth/me"),
  listChildren: () => request<components["schemas"]["ChildResponse"][]>("/auth/children"),
  createChild: (username: string, age: number) =>
    request<components["schemas"]["ChildResponse"]>("/auth/children", {
      method: "POST",
      body: JSON.stringify({ username, age }),
    }),
  /** Create a child profile with full payload (used by the Family tab and
   *  signup-flow inline child block). The simpler ``createChild`` above is
   *  retained for callers that only have username + age. */
  createChildProfile: (body: {
    username: string;
    birthdate: string;
    password?: string;
    email?: string;
  }) =>
    request<components["schemas"]["ChildResponse"]>("/auth/children", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Issue a password-reset token and email it to the verified address. */
  requestPasswordReset: () =>
    request<components["schemas"]["PasswordResetRequestResponse"]>("/auth/password-reset/request", {
      method: "POST",
    }),
  /** Redeem a password-reset token (public — no auth header required). */
  redeemPasswordReset: (token: string, newPassword: string) =>
    request<components["schemas"]["PasswordResetRedeemResponse"]>("/auth/password-reset/redeem", {
      method: "POST",
      body: JSON.stringify({ token, new_password: newPassword }),
    }),
};

// Projects
export interface CreateProjectBody {
  title: string;
  project_type: string;
  /**
   * creation-aspect-ratio (design.md §4.1). Default 'native' = today's
   * full-screen Create-here feel. Fixed aspects are explicitly chosen via
   * the chevron popover. `viewport` is REQUIRED when aspect_ratio === 'native'
   * (the server uses it as the captured design_space).
   */
  aspect_ratio?: "native" | "portrait_9_16" | "landscape_16_9" | "square_1_1";
  viewport?: { width: number; height: number };
}

/** A kind change that rewrote the page (U4): the editor takes `html` as a restore does. */
export type KindChange = components["schemas"]["KindChangeResponse"];

export const projects = {
  list: () => request<Project[]>("/projects"),
  get: (id: string) => request<Project>(`/projects/${id}`),
  create: (body: CreateProjectBody) =>
    request<Project>("/projects", { method: "POST", body: JSON.stringify(body) }),
  /**
   * Reframe an existing project — aspect is stored, never locked. The
   * runtime hot-swaps via the `__designSpaceScaled` event; the iframe stays
   * mounted. See `.kiro/specs/creation-aspect-ratio/design.md §3.4`.
   */
  updateAspect: (
    id: string,
    body: {
      aspect_ratio: "native" | "portrait_9_16" | "landscape_16_9" | "square_1_1";
      viewport?: { width: number; height: number };
    },
  ) =>
    request<Project>(`/projects/${id}/aspect`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  update: (id: string, data: { title?: string; project_type?: string; building_template?: string; type?: string; category?: string; by?: "chip" }) =>

    request<Project>(`/projects/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/projects/${id}`, { method: "DELETE" }),
  getHtml: (id: string) => request<{ html: string }>(`/projects/${id}/html`),
  uploadThumbnail: (id: string, image_data: string) =>
    request<Project>(`/projects/${id}/thumbnail`, { method: "POST", body: JSON.stringify({ image_data }) }),
  screenshot: (projectId: string, atMs?: number) =>
    request<Project>(
      `/projects/${projectId}/screenshot${atMs != null ? `?at_ms=${Math.max(0, Math.round(atMs))}` : ""}`,
      { method: "POST" },
    ),
  getVersions: (id: string, limit = 50, offset = 0) =>
    request<VersionListResponse>(`/projects/${id}/versions?limit=${limit}&offset=${offset}`),
  getVersionHtml: (id: string, versionNumber: number) =>
    request<components["schemas"]["VersionHtmlResponse"]>(
      `/projects/${id}/versions/${versionNumber}/html`,
    ),
  restoreVersion: (id: string, versionNumber: number) =>
    request<components["schemas"]["RestoreVersionResponse"]>(
      `/projects/${id}/versions/${versionNumber}/restore`, { method: "POST" }
    ),
  /** A Game back to a plain page, in place (project kinds U4): the goal rests in the save point. */
  restGame: (id: string) =>
    request<KindChange>(`/projects/${id}/rest-game`, { method: "POST", body: JSON.stringify({ by: "chip" }) }),
  /** "Bring it back" (U4): the save point's goal, into the page as it is now. */
  bringBackGoal: (id: string, savePoint: number) =>
    request<KindChange>(`/projects/${id}/versions/${savePoint}/bring-back-goal`, {
      method: "POST", body: JSON.stringify({ by: "chip" }),
    }),
  // Per-viewer "@self" avatar resolution (integration-wave Feature 09 R6):
  // this viewer's avatar asset, falling back to the project owner's.
  selfAvatar: (id: string) =>
    request<{ asset_id: string | null; image_url: string | null }>(
      `/projects/${id}/self-avatar`,
    ),
  publish: (id: string) =>
    // `status` is a two-value union on BOTH sides now: the route's model carried `str` with
    // the values in a comment, while its own producer already declared the Literal.
    request<Wire<components["schemas"]["PublishResponse"]>>(`/projects/${id}/publish`, {
      method: "POST",
    }),
  /**
   * Toggle the anonymous-shareable-link surface for a public project
   * (public-shareable-link spec R0.3). 409 if scope ≠ public.
   */
  setPublicLink: (id: string, enabled: boolean) =>
    request<Wire<components["schemas"]["PublicLinkResponse"]>>(`/projects/${id}/public_link`, {
      method: "PUT",
      body: JSON.stringify({ enabled }),
    }),
};

// Vertical fork-on-switch (post-build type changes fork a sibling project)
export const verticalFork = {
  preview: (sourceProjectId: string, targetVertical: string) =>
    request<{
      source_project_id: string;
      source_title: string;
      source_vertical: string;
      target_vertical: string;
      would_carry_over: string[];
      would_lose: string[];
      would_keep_separately: string[];
    }>(`/vertical-fork/preview`, {
      method: "POST",
      body: JSON.stringify({ source_project_id: sourceProjectId, target_vertical: targetVertical }),
    }),
  execute: (sourceProjectId: string, targetVertical: string) =>
    request<{ new_project_id: string; title: string; project_type: string; source_project_id: string }>(
      `/vertical-fork`,
      {
        method: "POST",
        body: JSON.stringify({ source_project_id: sourceProjectId, target_vertical: targetVertical }),
      },
    ),
};

// Sessions
export const sessions = {
  /**
   * `inputCapabilities` rides the dispatch metadata to the agent, so the model
   * knows whether THIS child has a keyboard before it binds an action to one
   * (cross-device-controls design.md). Two booleans; never a device class.
   */
  token: (project_id?: string, inputCapabilities?: { keys: boolean; touch: boolean }) =>
    request<{ token: string; room_name: string; session_id: string; livekit_url: string }>("/sessions/token", {
      method: "POST",
      body: JSON.stringify({
        project_id: project_id || null,
        ...(inputCapabilities
          ? { input_capabilities: { keys: inputCapabilities.keys, touch: inputCapabilities.touch } }
          : {}),
      }),
    }),
  end: (id: string) => request<void>(`/sessions/${id}/end`, { method: "POST" }),
  /** Whether a session still runs. 404 when it is not the caller's or is gone. */
  status: (id: string) => request<{ id: string; status: "active" | "ended"; ended_at: string | null }>(`/sessions/${id}/status`),
  endAllMine: () => request<{ status: string }>("/sessions/end-all-mine", { method: "POST" }),
};

// --- Co-Creation (multiplayer) ---
export interface CoEditJoinResponse {
  token: string;
  room_name: string;
  session_id: string;
  role: string;
  is_new: boolean;
  livekit_url: string;
}

export interface CoEditGrant {
  id: string;
  scope: string;
  grantee_user_id: string | null;
  project_id: string | null;
}

export const coCreation = {
  join: (projectId: string) =>
    request<CoEditJoinResponse>(`/projects/${projectId}/coedit/join`, { method: "POST" }),
  leave: (sessionId: string) =>
    request<{ ended: boolean; remaining: number }>(`/coedit/${sessionId}/leave`, { method: "POST" }),
  setIntentMode: (sessionId: string, mode: "chat" | "command") =>
    request<{ intent_mode: string }>(`/coedit/${sessionId}/intent-mode`, {
      method: "POST", body: JSON.stringify({ intent_mode: mode }),
    }),
  listGrants: (projectId: string) =>
    request<{ grants: CoEditGrant[] }>(`/projects/${projectId}/coedit/grants`),
  createGrant: (
    projectId: string,
    body: { scope: string; grantee_user_id?: string; project_id?: string },
  ) =>
    request<{ id: string; scope: string }>(`/projects/${projectId}/coedit/grants`, {
      method: "POST", body: JSON.stringify(body),
    }),
  revokeGrant: (grantId: string) =>
    request<{ revoked: boolean }>(`/coedit/grants/${grantId}`, { method: "DELETE" }),
  setVoiceOverride: (body: { friend_user_id: string; mode: "allow" | "deny" }) =>
    request<{ friend_user_id: string; mode: string }>(`/coedit/voice-overrides`, {
      method: "POST", body: JSON.stringify(body),
    }),
  // Entry points (integration-wave Feature 14 R4).
  invite: (projectId: string, friendUserId: string) =>
    request<{ status: string; grant_id?: string | null; request_id?: string }>(
      `/projects/${projectId}/coedit/invite`,
      { method: "POST", body: JSON.stringify({ friend_user_id: friendUserId }) },
    ),
  requestJoin: (projectId: string) =>
    request<{ status: string }>(`/projects/${projectId}/coedit/join-request`, {
      method: "POST",
    }),
  status: (projectId: string) =>
    request<CoEditStatus>(`/projects/${projectId}/coedit/status`),
};

export interface CoEditStatus {
  /** Present (and false) only when co-creation is switched off on this
   *  deployment; the rest of the fields are then absent. */
  enabled?: false;
  session_active: boolean;
  participants: Array<{
    user_id: string;
    role: string;
    intent_mode?: string;
    live_voice?: boolean;
  }>;
  can_join: boolean;
  can_request: boolean;
  session_full: boolean;
  /** True when the caller owns this project. Distinguishes "a grown-up turned
   * co-creation off" (your own build) from "their build isn't open to you"
   * (a friend's) — both of which read as can_join=false, can_request=false. */
  is_owner?: boolean;
}

// Shared co-creation queue over BuildPlan (integration-wave Feature 14).
export const buildPlanQueue = {
  get: (projectId: string) =>
    request<{ queue: Array<{ plan_id: string; label: string; requested_by: string | null; position: number }> }>(
      `/build-plans/queue?project_id=${encodeURIComponent(projectId)}`,
    ),
  reorder: (projectId: string, orderedPlanIds: string[]) =>
    request<{ ok: boolean }>(`/build-plans/queue/reorder`, {
      method: "POST",
      body: JSON.stringify({ project_id: projectId, ordered_plan_ids: orderedPlanIds }),
    }),
  cancelQueued: (planId: string) =>
    request<{ ok: boolean }>(`/build-plans/${planId}/cancel-queued`, { method: "POST" }),
  /** Durably dismiss an active/paused (or stalled) plan — "Okay, put it away"
   * (Thread A). Distinct from cancelQueued, which only drops a not-yet-started
   * queued plan. */
  cancel: (planId: string) =>
    request<{ ok: boolean }>(`/build-plans/${planId}/cancel`, { method: "POST" }),
  reorderTasks: (planId: string, orderedTaskIds: string[]) =>
    request<{ ok: boolean }>(`/build-plans/${planId}/tasks/reorder`, {
      method: "POST",
      body: JSON.stringify({ ordered_task_ids: orderedTaskIds }),
    }),
  editTask: (planId: string, taskId: string, description: string) =>
    request<{ ok: boolean }>(`/build-plans/${planId}/tasks/${taskId}`, {
      method: "PATCH",
      body: JSON.stringify({ description }),
    }),
};

/**
 * The project row, as the route declares it (`ProjectResponse` /
 * `ProjectDetailResponse`, backend/app/routes/projects.py). Hand-written until 2026-10-29,
 * when its ghost `user_id` -- a field no project route has ever returned -- was found to have
 * produced three live defects, two of them still live.
 */
export type Project = components["schemas"]["ProjectResponse"];
export type ProjectDetail = components["schemas"]["ProjectDetailResponse"];

/** One row of version history, from the route's own model (declared 2026-10-29).
 *
 * This was hand-written and it was MORE precise than the route: `kind` was a union here while
 * the route said `str`, which is why the swap had to wait for the route to declare the Literal.
 * Its `"restore"` member was also a value the backend never wrote — the restore route writes it
 * now, so the panel's glyph is reachable instead of dead. */
export type VersionEntry = components["schemas"]["VersionEntry"];

export type VersionListResponse = components["schemas"]["VersionListResponse"];

export interface ObservabilityEventPayload {
  event_type: string;
  user_id?: string;
  event_data?: Record<string, unknown>;
  session_id?: string;
  project_id?: string;
  voice_session_id?: string;
  utterance_id?: string;
  nav_sequence?: number;
  utterance_sequence?: number;
  turn_sequence?: number;
  mutation_sequence?: number;
  suppress_content?: boolean;
  event_ts?: string;
}

// Build Plan (Slice 4 SP-T4.6 / SP-T4.7; integration-wave Feature 05 adds
// category + wire-state + the per-project current-plan hydration route)
export interface PlanTaskApi {
  task_id: string;
  sequence: number;
  description: string;
  category?: string | null;
  status: "pending" | "active" | "completed" | "skipped" | "failed";
  state?: "waiting" | "working" | "done" | "skipped" | "failed";
  started_at: string | null;
  completed_at: string | null;
  created_by: string;
}
export interface PlanSnapshotApi {
  plan_id: string;
  project_id: string;
  session_id: string;
  status: "active" | "paused" | "completed" | "cancelled";
  spoken_summary: string;
  tasks: PlanTaskApi[];
}
export const buildPlans = {
  get: (planId: string) => request<PlanSnapshotApi>(`/build-plans/${planId}`),
  getCurrentForProject: (projectId: string) =>
    request<PlanSnapshotApi>(`/projects/${projectId}/plan`),
};

// Doctor (Slice 5 SP-T5.8)
export interface DoctorFixButtonApi {
  label: string;
  finding_code: string;
  repair_intent: string;
}
export const doctor = {
  buttons: (projectId: string, vertical: string = "generic") =>
    request<{ buttons: DoctorFixButtonApi[] }>(
      `/doctor/buttons?project_id=${encodeURIComponent(projectId)}&vertical=${encodeURIComponent(vertical)}`,
    ),
  repairContext: (projectId: string, vertical: string = "generic") =>
    request<Record<string, unknown>>(
      `/doctor/repair-context?project_id=${encodeURIComponent(projectId)}&vertical=${encodeURIComponent(vertical)}`,
    ),
  // App-side error bridge (integration-wave Feature 06): wbm:error → tier 1.
  tier1: (projectId: string, vertical: string, code: string, message?: string) =>
    request<{ finding_id: string }>(`/doctor/tier1`, {
      method: "POST",
      body: JSON.stringify({
        project_id: projectId,
        vertical,
        code,
        severity: "error",
        context: message ? { message } : {},
      }),
    }),
  changes: (projectId: string, since?: string) =>
    request<{ changes: Array<{ at: string | null; kind: string; summary: string }> }>(
      `/projects/${projectId}/changes${since ? `?since=${encodeURIComponent(since)}` : ""}`,
    ),
};

// Asset library — "My Stuff" (integration-wave Feature 08)
export interface AssetLibraryHitApi {
  asset_id: string;
  description: string;
  kind: string;
  image_url: string;
  score: number;
  needs_review: boolean;
  render_model: string;
  /** 'my_avatar' = the synthesized "Me" asset (Feature 09) — inserted into
   * specs as the per-viewer sentinel "@self", never its concrete id. */
  entity_name?: string | null;
  /** Walk spritesheet (avatar/pet) — when set, the tile shows the in-game
   * sprite (frame 0) instead of the static portrait. */
  sprite_sheet_url?: string | null;
  sprite_frame_count?: number | null;
  owner: { user_id: string; name: string } | null;
}
export const assetLibrary = {
  recent: (scope: "mine" | "friends" | "all" = "mine", kind?: string) =>
    request<{ hits: AssetLibraryHitApi[] }>(
      `/asset-library/recent?scope=${scope}${kind ? `&kind=${encodeURIComponent(kind)}` : ""}`,
    ),
  search: (q: string, scope: "mine" | "friends" | "all" = "mine", kind?: string) =>
    request<{ hits: AssetLibraryHitApi[] }>(
      `/asset-library/search?q=${encodeURIComponent(q)}&scope=${scope}${kind ? `&kind=${encodeURIComponent(kind)}` : ""}`,
    ),
  me: () => request<{ hits: AssetLibraryHitApi[] }>(`/asset-library/me`),
  // Parity with the agent-side reuse resolver (the agent calls this endpoint
  // server-to-server); unused by the chrome today but kept for tooling/e2e.
  resolve: (description: string, kind: string) =>
    request<{ candidates: AssetLibraryHitApi[] }>(`/asset-library/resolve`, {
      method: "POST",
      body: JSON.stringify({ description, kind }),
    }),
};

// Project save-state (integration-wave Feature 12). The chrome host persists
// over its authenticated session in response to the iframe's wbm:save.
//
// All four shapes come from the route's own models, declared 2026-10-30. Until then this file
// invented them: `player_state.py` returned bare dicts and the OpenAPI document had no entry
// for any of the four, so a renamed key was invisible on both sides.
/** One slot's metadata — never the saved `state` body. The route's model is what keeps that
 *  promise; `TestResponseVocabulary` in `test_unit_player_state.py` holds it to it. */
export type SaveSlotSummary = components["schemas"]["SaveSlotSummary"];
export const playerState = {
  list: (projectId: string) =>
    request<components["schemas"]["SaveSlotListResponse"]>(`/projects/${projectId}/state`),
  get: (projectId: string, slot: string) =>
    request<components["schemas"]["SaveSlotStateResponse"]>(
      `/projects/${projectId}/state/${encodeURIComponent(slot)}`,
    ),
  put: (projectId: string, slot: string, state: unknown, ifMatchVersion?: number) =>
    request<components["schemas"]["SaveSlotWriteResponse"]>(
      `/projects/${projectId}/state/${encodeURIComponent(slot)}`,
      {
        method: "PUT",
        body: JSON.stringify({ state }),
        headers:
          ifMatchVersion != null ? { "If-Match": String(ifMatchVersion) } : undefined,
      },
    ),
  remove: (projectId: string, slot: string) =>
    request<components["schemas"]["SaveSlotDeletedResponse"]>(
      `/projects/${projectId}/state/${encodeURIComponent(slot)}`,
      { method: "DELETE" },
    ),
};

// Runtime multiplayer (integration-wave Feature 13). Invite-driven lobby —
// no share codes in the kid UX (handoff). create returns the direct ws_url.
/** The room a kid just joined, from the route's own model, as the wire carries it. */
export type MpRoom = Wire<components["schemas"]["JoinResponse"]>;
export const multiplayer = {
  createRoom: (projectId: string, mode = "cooperative", maxPlayers = 2) =>
    request<MpRoom>(`/multiplayer/rooms`, {
      method: "POST",
      body: JSON.stringify({ project_id: projectId, mode, max_players: maxPlayers }),
    }),
  join: (shareCode: string) =>
    request<MpRoom>(`/multiplayer/rooms/${encodeURIComponent(shareCode)}/join`, {
      method: "POST",
    }),
  invite: (roomId: string, friendUserId: string) =>
    request<{ ok: boolean }>(`/multiplayer/rooms/${roomId}/invites`, {
      method: "POST",
      body: JSON.stringify({ friend_user_id: friendUserId }),
    }),
  leave: (roomId: string) =>
    request<{ ok: boolean }>(`/multiplayer/rooms/${roomId}/leave`, { method: "POST" }),
};

// Sandbox capability tokens (integration-wave Feature 11) — the host fetches a
// scoped, short-lived token and posts it into the opaque-origin iframe.
export const sandbox = {
  capabilityToken: (
    projectId: string,
    // `read_spec` = the LIVE spec (owner / co-editor only).
    // `read_published_spec` = the moderated published SNAPSHOT, for an
    // authenticated visitor in the city or a friend's town. Deliberately
    // separate capabilities: holding the visitor's one can never yield the
    // maker's mid-edit work.
    caps: Array<"save_state" | "multiplayer" | "read_spec" | "read_published_spec">,
    roomId?: string,
  ) =>
    request<{ token: string; expires_in: number }>(`/sandbox/capability-token`, {
      method: "POST",
      body: JSON.stringify({ project_id: projectId, caps, room_id: roomId }),
    }),
};

// Pets (integration-wave Feature 10). REST mapping (EXECUTION_PLAN Surface 7):
// design GET /me/pets ⇒ GET /pets; PUT /me/active-pet ⇒ POST /pets/{id}/activate.
export interface PetApi {
  pet_id: string;
  name: string;
  is_active: boolean;
  smart_asset_id: string;
  compilation_status: "pending" | "completed" | "failed";
  render_model: string;
  image_url: string | null;
  manifest: Record<string, unknown>;
  created_at: string | null;
}
// ─── Lands & Residents (.kiro/specs/lands-and-residents) ─────────────────────
export type LandEvent =
  | { type: "zone_entered"; land: string; place?: string | null }
  | { type: "resident_met"; resident: string }
  | { type: "seed_chosen"; seed: string }
  | { type: "project_saved"; project_id: string }
  | { type: "wakeup_seen"; wakeup_id: string; played?: boolean }
  | { type: "arrival_seen"; land: string }
  | { type: "capability_used"; tags: string[] }
  /** B0 (MAP_MARKERS §2.1): marks whose thing was in view for 1 s; the server unions known ids. */
  | { type: "marks_seen"; objects?: string[]; place?: string | null };

export type LandProjectOut = components["schemas"]["LandProjectOut"];

/** A music building's preview: its project's first music bed, or its first notes. */
export type CityMusicPreview = NonNullable<LandProjectOut["music"]>;

export const lands = {
  content: () => request<import("./world/lands/content").LandsBundle>("/lands/content"),
  // `/lands/record` and `/lands/record/events` are hand-written ON PURPOSE, and kept equal to
  // the generated shape by `world/lands/__tests__/landRecordWireParity.test.ts`. Their inner
  // types are the ENGINE's — `LandRecord` and `WakeupEvent` from `world/lands/derive`, read in
  // 34 files, whose doc comments are the record's spec — so an alias would drop those comments
  // and make every schema regen a Lands-wide type churn (the engine lane's call, 2026-10-30).
  // What they may not do is drift: that test fails naming the field and which side has it.
  record: () =>
    request<{
      record: import("./world/lands/derive").LandRecord;
      pending_wakeups: import("./world/lands/derive").WakeupEvent[];
      audio: Record<string, string>;
      occasions: string[];
    }>("/lands/record"),
  event: (event: LandEvent) =>
    request<{ record: import("./world/lands/derive").LandRecord; wakeups: import("./world/lands/derive").WakeupEvent[] }>(
      "/lands/record/events",
      { method: "POST", body: JSON.stringify(event) },
    ),
  projects: (land: string, place?: string) =>
    request<LandProjectOut[]>(`/lands/projects?land=${encodeURIComponent(land)}${place ? `&place=${encodeURIComponent(place)}` : ""}`),
  outskirts: (land: string, place: string) =>
    request<LandProjectOut[]>(`/lands/projects/outskirts?land=${encodeURIComponent(land)}&place=${encodeURIComponent(place)}`),
  /** Part B: a step the frame met; recorded only from the saved page (CHALLENGE_MODEL §5). */
  missionStep: (projectId: string, step: string) =>
    request<Wire<components["schemas"]["MissionStepOut"]>>(
      `/lands/mission/${projectId}/step`, { method: "POST", body: JSON.stringify({ step }) }),
  createFromSeed: (body: { seed_id: string; lot: string; place?: string; title?: string; viewport?: { width: number; height: number } }) =>
    request<Project>("/lands/projects", { method: "POST", body: JSON.stringify(body) }),
  /** The robot grid's plan, as the kid ran it in play (`wbm:plan`, CHALLENGE_MODEL §7): saved
   *  and judged as a save is. Owner only; the server refuses anyone else's project. */
  savePlan: (projectId: string, steps: string[]) =>
    request<Wire<components["schemas"]["MissionJudgeOut"]>>(
      `/lands/projects/${encodeURIComponent(projectId)}/plan`,
      { method: "POST", body: JSON.stringify({ steps }) },
    ),
  /** B15, the Square's noticeboard: the street holding the City's nearest building of a type. */
  cityWhere: (category: string) =>
    request<components["schemas"]["CityWhereOut"] | null>(
      `/lands/city/where?category=${encodeURIComponent(category)}`,
    ),
};

export const pets = {
  list: () => request<{ pets: PetApi[] }>(`/pets`),
  create: (description: string, name?: string) =>
    request<PetApi>(`/pets`, {
      method: "POST",
      body: JSON.stringify({ description, name }),
    }),
  activate: (petId: string) =>
    request<PetApi>(`/pets/${petId}/activate`, { method: "POST" }),
  remove: (petId: string) =>
    request<{ removed: boolean }>(`/pets/${petId}`, { method: "DELETE" }),
};

// SmartAsset corrections (Slice 5 SP-T5.7)
export const smartAssets = {
  get: (assetId: string) =>
    request<{ asset_id: string; kind: string; manifest: Record<string, unknown> }>(
      `/smart-assets/${assetId}`,
    ),
  correctAnchor: (assetId: string, anchorName: string, point: [number, number]) =>
    request<{ ok: boolean; anchor: unknown }>(
      `/smart-assets/${assetId}/anchors/${encodeURIComponent(anchorName)}`,
      { method: "POST", body: JSON.stringify({ point }) },
    ),
  correctSensor: (assetId: string, sensorName: string, shape: Record<string, unknown>) =>
    request<{ ok: boolean; sensor: unknown }>(
      `/smart-assets/${assetId}/sensors/${encodeURIComponent(sensorName)}`,
      { method: "POST", body: JSON.stringify({ shape }) },
    ),
};

// Observability
export const observability = {
  emit: (event: ObservabilityEventPayload) => {
    if (!event.user_id) return Promise.resolve(undefined as void);
    // Telemetry is fire-and-forget and must never surface as an error. Most callers do not
    // catch, so a failed POST (dev's API under load, offline) became an uncaught rejection --
    // a page error on every Lands walk (lands-walk.mjs, 2026-10-18).
    return request<void>("/observability/events", {
      method: "POST",
      body: JSON.stringify(event),
    }).catch(() => undefined);
  },
};

// Demo → account project carryover (demo-project-carryover).
// Fired once at signup completion to re-home the demo project the visitor built
// into their brand-new account. Authorized by the new account's Bearer token
// (set via setAccessToken right after /auth/register). Callers treat any failure
// as non-fatal — signup must still complete (Req 10).
export const demo = {
  claim: (demoToken: string, childIds: string[] = []) =>
    request<{
      carried: boolean;
      project_id?: string;
      reason?: string;
      copies_created?: number;
    }>("/demo/claim", {
      method: "POST",
      body: JSON.stringify({ demo_token: demoToken, child_ids: childIds }),
    }),
};

// Family profiles and child auth
export interface ParentProfileItem {
  id: string;
  username: string;
  is_current_user: boolean;
}

export interface ChildProfileItem {
  id: string;
  username: string;
  age: number;
  has_passphrase: boolean;
  has_spoken_variant: boolean;
  avatar_url: string | null;
  /** When the kid was added (seat unit: kids hold seats in this order). Optional: an older backend omits it. */
  created_at?: string | null;
}

export type ProfileListResponse = components["schemas"]["ProfileListResponse"];

export type ChildSessionResponse = components["schemas"]["ChildSessionResponse"];

// Child sessions use the same token slot — no separate variable needed.
export function setChildSessionToken(token: string) {
  accessToken = token;
  isChildToken = true;
  try { sessionStorage.setItem("child_session_token", token); sessionStorage.setItem("is_child_token", "1"); } catch { /* SSR/test */ }
}
export function getChildSessionToken() { return accessToken; }
export function clearChildSession() {
  accessToken = "";
  isChildToken = false;
  try { sessionStorage.removeItem("child_session_token"); sessionStorage.removeItem("is_child_token"); } catch { /* SSR/test */ }
}

/** The parent-verification routes' own response models (declared 2026-10-29). The status one
 *  carries the four values the column is ever written with — surveyed on dev AND prod before it
 *  was narrowed, because this is the route the consent gate polls. */
export type VerificationStatusResponse = components["schemas"]["VerificationStatusResponse"];
export type VerificationSupportItem = components["schemas"]["VerificationSupportItem"];

/** The family routes' own response models (declared 2026-10-29). Each of these was a
 *  hand-written shape or a `void` that said nothing; `AcceptInvitationResponse` replaces a
 *  `{ access_token: string }` the route has never sent. */
export type AcceptInvitationResponse = components["schemas"]["AcceptInvitationResponse"];
export type UsernameSetResponse = components["schemas"]["UsernameSetResponse"];
export type PassphraseUpdatedResponse = components["schemas"]["PassphraseUpdatedResponse"];
export type PasswordUpdatedResponse = components["schemas"]["PasswordUpdatedResponse"];
export type SpokenVariantRecordedResponse =
  components["schemas"]["SpokenVariantRecordedResponse"];
export type UpdateChildProfileResponse = components["schemas"]["UpdateChildProfileResponse"];

export const family = {
  getProfiles: () =>
    request<ProfileListResponse>("/family/profiles"),

  deleteChild: (childId: string, password: string) =>
    request<void>(`/family/children/${childId}`, {
      method: "DELETE",
      body: JSON.stringify({ password }),
    }),

  restoreChild: (childId: string) =>
    request<void>(`/family/children/${childId}/restore`, { method: "POST" }),

  getDeletedChildren: () =>
    request<{ id: string; username: string; deleted_at: string; days_remaining: number }[]>(
      "/family/children/deleted"
    ),

  setPassphrase: (childId: string, passphrase: string) =>
    request<PassphraseUpdatedResponse>(`/family/children/${childId}/passphrase`, {
      method: "PUT",
      body: JSON.stringify({ passphrase }),
    }),

  setChildPassword: (childId: string, password: string) =>
    request<PasswordUpdatedResponse>(`/family/children/${childId}/password`, {
      method: "PUT",
      body: JSON.stringify({ password }),
    }),

  /** Update one or more fields on a child profile (username / birthdate /
   *  email / password). Only the provided fields are touched. */
  updateChild: (
    childId: string,
    patch: { username?: string; birthdate?: string; email?: string; password?: string },
  ) =>
    request<UpdateChildProfileResponse>(`/family/children/${childId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  recordSpokenVariant: (childId: string, audioData: string) =>
    request<SpokenVariantRecordedResponse>(
      `/family/children/${childId}/passphrase/spoken-variant`,
      {
        method: "POST",
        body: JSON.stringify({ audio_data: audioData }),
      },
    ),

  sendInvitation: (email: string) =>
    request<void>("/family/invitations", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  listInvitations: () =>
    request<{ id: string; invited_email: string; status: string; expires_at: string }[]>(
      "/family/invitations"
    ),

  acceptInvitation: (token: string, email: string, password: string) =>
    request<AcceptInvitationResponse>("/family/invitations/accept", {
      method: "POST",
      body: JSON.stringify({ token, email, password }),
    }),
};

export const childAuth = {
  voiceLogin: (audioData: string, familyAccountId: string) =>
    request<ChildSessionResponse>("/auth/child/voice-login", {
      method: "POST",
      body: JSON.stringify({ audio_data: audioData, family_account_id: familyAccountId }),
    }),

  typedLogin: (displayName: string, passphrase: string, familyAccountId: string) =>
    request<ChildSessionResponse>("/auth/child/typed-login", {
      method: "POST",
      body: JSON.stringify({ username: displayName, passphrase, family_account_id: familyAccountId }),
    }),

  refresh: () =>
    request<ChildSessionResponse>("/auth/child/refresh", { method: "POST" }),
};

export const emailVerification = {
  verify: (token: string) =>
    request<void>("/auth/verify-email", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),

  resend: () =>
    request<void>("/auth/resend-verification", { method: "POST" }),
};

// Presence audio asset admin
export type AudioAssetCategory = "thinking_sound" | "filler_phrase" | "connection_sound" | "disconnection_sound" | "first_run_sound" | "acknowledgment_chime" | "signup_prompt";

export type AudioAsset = components["schemas"]["AdminAudioAssetResponse"];

// Shared multipart transport. Mirrors ``request()``'s two must-haves:
//   1. ``credentialsMode()`` so the HttpOnly access_token cookie
//      travels cross-subdomain (worldby.me → api.worldby.me). Without
//      it, uploads 401 whenever the in-memory ``accessToken`` module
//      var is empty — which is the normal state after any reload, since
//      that var is only set in memory by setAccessToken() post-login.
//   2. A 401-retry that runs ``attemptTokenRefresh()`` and replays the
//      original body, so an expired access token doesn't drop uploads
//      that the regular JSON path would have silently rescued.
async function _multipart<T>(
  method: "POST" | "PUT",
  path: string,
  body: FormData,
): Promise<T> {
  const headers: Record<string, string> = { ...clientHeaders() };
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
  const credentials = credentialsMode();
  const res = await fetch(`${BASE}${path}`, { method, headers, body, credentials });
  if (res.status === 401 && !path.startsWith("/auth/")) {
    const refreshed = await attemptTokenRefresh();
    if (refreshed) {
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
      const retry = await fetch(`${BASE}${path}`, { method, headers, body, credentials });
      if (!retry.ok) {
        throw buildApiError(
          await retry.json().catch(() => ({})),
          `HTTP ${retry.status}`,
          retry.status,
        );
      }
      return retry.json();
    }
  }
  if (!res.ok) {
    throw buildApiError(await res.json().catch(() => ({})), `HTTP ${res.status}`, res.status);
  }
  return res.json();
}

async function requestMultipart<T>(path: string, body: FormData): Promise<T> {
  return _multipart<T>("POST", path, body);
}

async function requestMultipartPut<T>(path: string, body: FormData): Promise<T> {
  return _multipart<T>("PUT", path, body);
}

export const presenceAdmin = {
  listAssets: () =>
    request<AudioAsset[]>("/presence/admin/audio-assets"),

  uploadAsset: (file: File, category: AudioAssetCategory, display_name: string) => {
    const form = new FormData();
    form.append("file", file);
    form.append("category", category);
    form.append("display_name", display_name);
    return requestMultipart<AudioAsset>("/presence/admin/audio-assets", form);
  },

  replaceAsset: (id: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return requestMultipartPut<AudioAsset>(`/presence/admin/audio-assets/${id}`, form);
  },

  toggleActive: (id: string) =>
    request<AudioAsset>(`/presence/admin/audio-assets/${id}/active`, { method: "PATCH" }),

  getPresignedUrl: (id: string) =>
    request<{ url: string }>(`/presence/admin/audio-assets/${id}/presigned-url`),

  generateAsset: (text: string, category: AudioAssetCategory, displayName: string, voiceId?: string) =>
    request<AudioAsset>("/presence/admin/audio-assets/generate", {
      method: "POST",
      body: JSON.stringify({ text, category, username: displayName, voice_id: voiceId || "" }),
    }),

  deleteAsset: (id: string) =>
    request<void>(`/presence/admin/audio-assets/${id}`, { method: "DELETE" }),
};

// --- Social Features: Discovery, Friendships, Sharing, Notifications, Admin ---

export type AccountSummary = components["schemas"]["AccountSummaryResponse"];

export type FriendRequest = components["schemas"]["FriendRequestResponse"];

export interface FriendResponse {
  account_id: string;
  username: string;
  account_type: string;
  avatar_url?: string;
}

export interface SharedProject {
  id: string;
  title: string;
  project_type: string;
  creator_username?: string;
  shared_at?: string;
  sharing_scope: string;
}

export type ProvenanceEntry = components["schemas"]["ProvenanceEntryResponse"];

export type Notification = components["schemas"]["NotificationResponse"];

export type ChannelPreferences = components["schemas"]["ChannelPreferencesResponse"];

// Discovery
export const discovery = {
  getMyFriendCode: () =>
    request<{ friend_code: string }>("/discovery/my-code"),

  lookupByCode: (friend_code: string) =>
    request<AccountSummary>("/discovery/lookup", {
      method: "POST",
      body: JSON.stringify({ friend_code }),
    }),

  searchByDisplayName: (q: string) =>
    request<{ results: AccountSummary[] }>(`/discovery/search?q=${encodeURIComponent(q)}`),

  emailLookup: (email: string) =>
    request<{ request_queued: boolean }>("/discovery/email-lookup", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  getProjectOwner: (projectId: string) =>
    request<AccountSummary>(`/discovery/project-owner/${projectId}`),

  getProvenanceCreators: (projectId: string) =>
    request<AccountSummary[]>(`/discovery/provenance-creators/${projectId}`),
};

// Friendships
export const friendships = {
  sendRequest: (toAccountId: string) =>
    request<FriendRequest>("/friendships/requests", {
      method: "POST",
      body: JSON.stringify({ to_account_id: toAccountId }),
    }),

  acceptRequest: (requestId: string) =>
    request<{ status: string }>(`/friendships/requests/${requestId}/accept`, { method: "POST" }),

  rejectRequest: (requestId: string) =>
    request<{ status: string }>(`/friendships/requests/${requestId}/reject`, { method: "POST" }),

  getPendingRequests: () =>
    request<FriendRequest[]>("/friendships/requests/pending"),

  getSentRequests: () =>
    request<string[]>("/friendships/requests/sent"),

  removeFriend: (friendId: string) =>
    request<void>(`/friendships/${friendId}`, { method: "DELETE" }),

  getFriends: () =>
    request<{ account_id: string; username: string; account_type: string; avatar_url?: string }[]>("/friendships"),

  setFriendshipPermission: (accountId: string, permission: string) =>
    request<void>(`/accounts/${accountId}/friendship-permission`, {
      method: "PUT",
      body: JSON.stringify({ permission }),
    }),
};

// Sharing
/** One saved project, as the Saved sheet shows it (design-share-save.md). */
export interface SavedItemWire {
  project_id: string;
  title: string;
  thumbnail_url: string | null;
  maker_display_name: string;
  saved_at: string;
}

/**
 * Save: a reference to a shared project (game-on-freeform). Works from the share page's
 * island as well as the app: `request` carries the Bearer token when the app holds one and
 * the session cookie otherwise, so a signed-in visitor on worldby.me/share/<id> is known.
 */
export const saved = {
  state: (projectId: string) =>
    request<{ saved: boolean; mine: boolean; shareable: boolean; viewer?: "child" | "grownup" | null }>(`/saved/${projectId}`),
  save: (projectId: string, via: "button" | "signup" | "signup_sheet" = "button") =>
    request<{ saved: true; first: boolean }>("/saved", {
      method: "POST",
      body: JSON.stringify({ project_id: projectId, via }),
    }),
  unsave: (projectId: string) => request<{ saved: false }>(`/saved/${projectId}`, { method: "DELETE" }),
  list: () => request<{ items: SavedItemWire[] }>("/saved"),
};

export const sharing = {
  shareProject: (projectId: string, scope: "friends" | "public") =>
    request<{ status: string; scope: string }>(`/projects/${projectId}/share`, {
      method: "POST",
      body: JSON.stringify({ scope }),
    }),

  unshareProject: (projectId: string, retainCredit = true) =>
    request<{ status: string }>(`/projects/${projectId}/unshare`, {
      method: "POST",
      body: JSON.stringify({ retain_credit: retainCredit }),
    }),

  setBuildingVisibility: (projectId: string, visibility: string) =>
    request<{ status: string }>(`/projects/${projectId}/visibility`, {
      method: "PUT",
      // Send the canonical string directly — backend column is NOT NULL with
      // server default 'account_default', so "account_default" round-trips
      // identically to what's read on load. (Older builds sent null here, which
      // hit the NOT NULL constraint and 500'd.)
      body: JSON.stringify({ building_visibility_override: visibility }),
    }),

  getFriendsProjects: () =>
    request<SharedProject[]>("/projects/friends"),

  getPublicProjects: () =>
    request<SharedProject[]>("/projects/public"),

  viewProject: (projectId: string) =>
    request<SharedProject>(`/projects/${projectId}/view`),

  remixProject: (projectId: string) =>
    request<SharedProject>(`/projects/${projectId}/remix`, { method: "POST" }),

  getProvenance: (projectId: string) =>
    request<ProvenanceEntry[]>(`/projects/${projectId}/provenance`),

  setSharingPermission: (accountId: string, permission: string) =>
    request<void>(`/accounts/${accountId}/sharing-permission`, {
      method: "PUT",
      body: JSON.stringify({ permission }),
    }),

  setCreditRetention: (accountId: string, setting: "retain" | "redact") =>
    request<void>(`/accounts/${accountId}/credit-retention`, {
      method: "PUT",
      body: JSON.stringify({ setting }),
    }),
};

// Parent social oversight
export const parentSocial = {
  getChildSharedContent: (childId: string) =>
    request<{ id: string; title: string; project_type: string; is_remix: boolean }[]>(
      `/parent/children/${childId}/shared-content`
    ),

  // `spec_vertical` (+ the framing pair) ride along because the oversight
  // surface has to choose a preview surface BEFORE it can render, and unlike
  // the city viewer it cannot ask `GET /projects/{id}` — a parent is not the
  // owner, a co-editor, or a friend of their own child, so that route refuses
  // them by design.
  getChildProjectsAudit: (childId: string) =>
    request<{
      id: string;
      title: string;
      thumbnail_url: string | null;
      owner_name: string;
      is_own: boolean;
      blocked: boolean;
      spec_vertical: string | null;
      aspect_ratio: AspectWire | null;
      design_space: DesignSpace | null;
    }[]>(`/parent/children/${childId}/projects-audit`),

  // Preview HTML for a project the child can access (child-owned OR
  // shared to them via friend network). Response is server-side
  // injected with the design-space runtime so agent-emitted __ds()
  // references don't throw at frame time.
  getChildProjectHtml: (childId: string, projectId: string) =>
    request<{ html: string }>(
      `/parent/children/${childId}/projects/${projectId}/html`
    ),

  blockProject: (childId: string, projectId: string) =>
    request<{ status: string }>(`/parent/children/${childId}/block-project/${projectId}`, { method: "POST" }),

  unblockProject: (childId: string, projectId: string) =>
    request<{ status: string }>(`/parent/children/${childId}/block-project/${projectId}`, { method: "DELETE" }),

  getDerivativesFromFriend: (childId: string, friendId: string) =>
    request<{ id: string; title: string; project_type: string; is_remix: boolean }[]>(
      `/parent/children/${childId}/derivatives-from/${friendId}`
    ),

  deleteChildProject: (childId: string, projectId: string) =>
    request<void>(`/parent/children/${childId}/projects/${projectId}`, { method: "DELETE" }),

  deleteAllDerivatives: (childId: string, friendId: string) =>
    request<{ deleted_count: number }>(
      `/parent/children/${childId}/derivatives-from/${friendId}/delete-all`,
      { method: "POST" }
    ),
};

// Notifications
export const notifications = {
  getNotifications: (limit = 50, offset = 0) =>
    request<Notification[]>(`/notifications?limit=${limit}&offset=${offset}`),

  getUnreadCount: () =>
    request<{ count: number }>("/notifications/unread-count"),

  markRead: (notificationId: string) =>
    request<{ status: string }>(`/notifications/${notificationId}/read`, { method: "POST" }),

  getChannelPreferences: () =>
    request<ChannelPreferences>("/notifications/preferences"),

  setChannelPreferences: (category: string, channels: string[]) =>
    request<{ status: string }>("/notifications/preferences", {
      method: "PUT",
      body: JSON.stringify({ category, channels }),
    }),

  /**
   * Toggle the "email" delivery channel for transactional events
   * (friend requests, parent approvals, weekly recaps for parents).
   *
   * Writes to ``personal`` for everyone and additionally to ``children``
   * for parent accounts. Other channels (``on_site``) are preserved.
   *
   * Validates: Requirements 23.4, 23.5.
   */
  setEmailChannel: async (next: boolean, opts: { isParent: boolean }) => {
    const prefs = await request<ChannelPreferences>("/notifications/preferences");
    const updateList = (current: string[] | null | undefined) => {
      const list = new Set(current ?? ["on_site"]);
      if (next) list.add("email");
      else list.delete("email");
      // Always retain at least on_site so the user keeps in-app notifications.
      list.add("on_site");
      return Array.from(list);
    };
    await request<{ status: string }>("/notifications/preferences", {
      method: "PUT",
      body: JSON.stringify({ category: "personal", channels: updateList(prefs.personal) }),
    });
    if (opts.isParent) {
      await request<{ status: string }>("/notifications/preferences", {
        method: "PUT",
        body: JSON.stringify({ category: "children", channels: updateList(prefs.children) }),
      });
    }
  },
};

// Friend presence (design-migration-completion §14.3)
export type FriendPresence = components["schemas"]["FriendPresenceResponse"];

export const presence = {
  /** Fetch the requesting user's friends with privacy-resolved presence. */
  getFriends: () => request<FriendPresence[]>("/presence/friends"),
};

// Building Templates (admin + public)
import type {
  BuildingTemplateCreateBody,
  BuildingTemplateOut,
  BuildingTemplateUpdateBody,
} from "./types/buildingTemplates";
import type { BuildingTemplate } from "./world/engine/sprites/types";
import { validateTemplate } from "./world/engine/sprites/templateSchema";

export type { BuildingTemplateOut, BuildingTemplateCreateBody, BuildingTemplateUpdateBody };

// The public template list, fetched ahead of the World (prefetchPublic). The town
// cannot draw until it has this list and every town image in it, and the World only
// asks once Phaser has booted; starting it as soon as a session is restored takes
// that wait off the cold load (ipad-native-shell device pass, 2026-10-21).
let publicTemplatesPrefetch: Promise<BuildingTemplate[]> | null = null;

export const buildingTemplates = {
  /** Start the public list now; idempotent. A failure forgets itself, so the
   *  registry's own fetch (and its fallback) still runs. */
  prefetchPublic: (): Promise<BuildingTemplate[]> => {
    if (!publicTemplatesPrefetch) {
      const p = buildingTemplates.getPublic();
      publicTemplatesPrefetch = p;
      p.catch(() => { if (publicTemplatesPrefetch === p) publicTemplatesPrefetch = null; });
    }
    return publicTemplatesPrefetch;
  },
  /** The prefetched list, once: the next World mount fetches a fresh one. */
  takePrefetchedPublic: (): Promise<BuildingTemplate[]> | null => {
    const p = publicTemplatesPrefetch;
    publicTemplatesPrefetch = null;
    return p;
  },
  /** List all non-deleted templates (admin). */
  list: () =>
    request<BuildingTemplateOut[]>("/admin/building-templates"),

  /** List all templates including soft-deleted (admin). */
  listIncludingDeleted: () =>
    request<BuildingTemplateOut[]>("/admin/building-templates?include_deleted=true"),

  /** Get a single template by id (admin). */
  get: (id: string) =>
    request<BuildingTemplateOut>(`/admin/building-templates/${id}`),

  /** Create a new template (admin). */
  create: (body: BuildingTemplateCreateBody) =>
    request<BuildingTemplateOut>("/admin/building-templates", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Update an existing template (admin). */
  update: (id: string, body: BuildingTemplateUpdateBody) =>
    request<BuildingTemplateOut>(`/admin/building-templates/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  /** Upload the Original_Image PNG (admin, multipart). */
  uploadOriginal: (id: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return requestMultipart<BuildingTemplateOut>(
      `/admin/building-templates/${id}/original`,
      form,
    );
  },

  /** Run background removal on the current Original_Image (admin). */
  removeBackground: (id: string) =>
    request<BuildingTemplateOut>(`/admin/building-templates/${id}/remove-background`, {
      method: "POST",
    }),

  /** Publish the template (admin). */
  publish: (id: string, crop?: { x: number; y: number; width: number; height: number } | null) =>
    request<BuildingTemplateOut>(`/admin/building-templates/${id}/publish`, {
      method: "POST",
      body: JSON.stringify(crop ? { crop } : {}),
    }),

  /** Unpublish the template (admin). */
  unpublish: (id: string) =>
    request<BuildingTemplateOut>(`/admin/building-templates/${id}/unpublish`, {
      method: "POST",
    }),

  /** Soft-delete a template (admin). */
  delete: (id: string) =>
    request<void>(`/admin/building-templates/${id}`, { method: "DELETE" }),

  /** Restore a soft-deleted template (admin). */
  restore: (id: string) =>
    request<BuildingTemplateOut>(`/admin/building-templates/${id}/restore`, {
      method: "POST",
    }),

  /**
   * Public listing of all published, non-deleted templates.
   * Each item is validated through `validateTemplate`; invalid items
   * are silently dropped (P8 symmetric counterpart on the client).
   */
  getPublic: async (): Promise<BuildingTemplate[]> => {
    const raw = await request<unknown[]>("/building-templates");
    const results: BuildingTemplate[] = [];
    for (const item of raw) {
      const validated = validateTemplate(item);
      if (validated !== null) {
        results.push(validated);
      } else {
        console.warn("[TemplateRegistry] validateTemplate rejected:", (item as { name?: string }).name);
      }
    }
    console.log("[TemplateRegistry] getPublic loaded", results.length, "of", raw.length, "templates");
    return results;
  },

  /** List published covered building templates (for appearance picker). */
  getCovered: async (): Promise<BuildingTemplate[]> => {
    const raw = await request<unknown[]>("/building-templates/covered");
    const results: BuildingTemplate[] = [];
    for (const item of raw) {
      const validated = validateTemplate(item);
      if (validated !== null) {
        results.push(validated);
      } else if (item && typeof item === "object") {
        const r = item as Record<string, unknown>;
        const pi = (r.published_image ?? r.original_image) as Record<string, unknown> | null;
        if (typeof r.name === "string" && pi && typeof pi === "object" && typeof pi.s3_key === "string") {
          results.push({
            id: typeof r.id === "string" ? r.id : undefined,
            name: r.name,
            version: typeof r.version === "number" ? r.version : 1,
            original_image: { s3_key: pi.s3_key, width: Number(pi.width) || 512, height: Number(pi.height) || 512 },
            published_image: { s3_key: pi.s3_key, width: Number(pi.width) || 512, height: Number(pi.height) || 512 },
            footprint_baseline: { left: { x: 0, y: 0 }, right: { x: 1, y: 0 } },
            slots: { door: { shape: "point", x: 0, y: 0 } },
            building_kind: "covered",
          } as BuildingTemplate);
        }
      }
    }
    return results;
  },
};

// ─── Blog admin ───────────────────────────────────────────────────────────
// Wraps the /blog/admin/* endpoints in backend/app/routes/blog.py. Caller
// must be authenticated as a user with users.is_content_editor = true;
// otherwise these calls 403. The lifecycle endpoints (submit, approve,
// publish, unpublish) each return the updated post serialization.

export type BlogPostStatus =
  | "ingested" | "planned" | "drafted"
  | "in_review" | "approved" | "published" | "rejected";

export interface BlogPostAdmin {
  id: string;
  status: BlogPostStatus;
  surface: string;
  slug: string | null;
  title: string | null;
  subtitle: string | null;
  summary: string | null;
  brief: string | null;
  draft_body: string | null;
  meta_title: string | null;
  meta_description: string | null;
  hero_image_url: string | null;
  hero_image_caption: string | null;
  hero_image_source: string | null;
  og_image_url: string | null;
  section: string | null;
  attribute_tags: string[];
  topic_tags: string[];
  pillar_slug: string | null;
  series_slug: string | null;
  embedded_creations: unknown[];
  author_id: string | null;
  author_display_name: string | null;
  published_version: number | null;
  ingested_at: string | null;
  planned_at: string | null;
  drafted_at: string | null;
  in_review_at: string | null;
  approved_at: string | null;
  published_at: string | null;
  rejected_at: string | null;
}

export interface BlogPostMetadataDraft {
  title: string | null;
  subtitle: string | null;
  summary: string | null;
  brief: string | null;
  slug: string | null;
  section: string | null;
  attribute_tags: string[];
  topic_tags: string[];
  pillar_slug: string | null;
  series_slug: string | null;
  hero_image_url: string | null;
  hero_image_caption: string | null;
  og_image_url: string | null;
  meta_title: string | null;
  meta_description: string | null;
  author_display_name: string;
}

export const blogAdmin = {
  list: (params?: { status?: BlogPostStatus; section?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status_", params.status);
    if (params?.section) qs.set("section", params.section);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<{ items: BlogPostAdmin[] }>(`/blog/admin/posts${suffix}`);
  },

  get: (id: string) =>
    request<BlogPostAdmin>(`/blog/admin/posts/${id}`),

  create: (payload: BlogPostMetadataDraft) =>
    request<BlogPostAdmin>("/blog/admin/posts", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  update: (id: string, payload: Partial<BlogPostMetadataDraft>) =>
    request<BlogPostAdmin>(`/blog/admin/posts/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  saveDraft: (id: string, draft_body: string, embedded_creations: unknown[] = []) =>
    request<BlogPostAdmin>(`/blog/admin/posts/${id}/draft`, {
      method: "POST",
      body: JSON.stringify({ draft_body, embedded_creations }),
    }),

  submit: (id: string) =>
    request<BlogPostAdmin>(`/blog/admin/posts/${id}/submit`, { method: "POST" }),

  approve: (id: string) =>
    request<BlogPostAdmin>(`/blog/admin/posts/${id}/approve`, { method: "POST" }),

  publish: (id: string) =>
    request<BlogPostAdmin>(`/blog/admin/posts/${id}/publish`, { method: "POST" }),

  unpublish: (id: string, reason: string) =>
    request<BlogPostAdmin>(`/blog/admin/posts/${id}/unpublish`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  reject: (id: string, reason: string) =>
    request<BlogPostAdmin>(`/blog/admin/posts/${id}/reject`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  mintPreviewToken: (id: string) =>
    request<{ url: string; expires_at_iso: string; ttl_seconds: number }>(
      `/blog/admin/posts/${id}/preview-token`,
      { method: "POST" },
    ),

  // hero-image-editor-integration/tasks.md F1 — thin adapter over the
  // review-tier routes POST /review/items/{id}/fetch_hero (returns a
  // TRAY of candidates) and POST /review/items/{id}/select_hero (writes
  // the operator's pick).
  fetchHero: (id: string, payload: { feedback_text?: string } = {}) =>
    request<FetchHeroResponse>(`/review/items/${id}/fetch_hero`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  selectHero: (id: string, payload: HeroSelectRequest) =>
    request<components["schemas"]["HeroSelected"]>(
      `/review/items/${id}/select_hero`,
      { method: "POST", body: JSON.stringify(payload) },
    ),
};

/** One image the chain found, from the route's own model. `source` is non-null because
 *  `ImageResult` requires `source_name` and its property raises without one — see
 *  `TestSourceNameContract`. */
export type HeroTrayCandidate = components["schemas"]["HeroTrayCandidateOut"];

export type HeroSelectRequest = components["schemas"]["SelectHeroRequest"];

export type FetchHeroResponse =
  | components["schemas"]["HeroFetched"]
  | components["schemas"]["HeroChainExhausted"]
  | components["schemas"]["HeroHonestyDeniedAll"];

// ─── Content review (the editor surface for AI-generated drafts) ──────────
// Wraps backend/app/routes/review.py (mounted at /review). Caller must be
// authenticated as a user with users.is_content_editor = true server-side;
// otherwise these calls 403.

export type ContentStatus =
  | "ingested" | "planned" | "drafted"
  | "in_review" | "approved" | "published" | "rejected";

export type RiskTier = "low" | "medium" | "high";

export type ReviewMode = "default" | "spotcheck" | "concerning" | "undispatched";

/** The latest `dispatch_failed` audit row on an approved-but-undispatched social row, from the
 *  queue route's own model. `error` is the refusal text — ours or the provider's — and the audit
 *  row is the only place it lands. */
export type DispatchFailureSummary = components["schemas"]["DispatchFailureSummary"];

/** What the dispatch chain says about a row, run WITHOUT dispatching.
 *  The same gates `POST /publishing/dispatch` enforces, so a pass here is
 *  a pass there. `applicable` is false for a surface that never
 *  dispatches (blog, newsletter) — not a failure. Lengths are as the
 *  platform counts them, on the body that would go on the wire (UTMs
 *  included, links weighted the way X weighs them). */
/** `GET /review/items/{id}/preflight`, from the route's own model (declared 2026-10-30).
 *  `reason` is the one key the route adds to `PreflightResult.as_dict()`, and only the
 *  dispatching branch sets it. */
export type DispatchPreflight = components["schemas"]["DispatchPreflightOut"];

export type SendBackDimension =
  | "voice"
  | "claims_honesty"
  | "values_alignment"
  | "connection_rule"
  | "provenance_honesty"
  | "operator_general";

export interface ReviewQueueRow {
  id: string;
  surface: string;
  section: string | null;
  risk_tier: RiskTier | null;
  title: string | null;
  author_display_name: string;
  in_review_at: string | null;
  published_at: string | null;
  latest_evaluator_decision: string | null;
  flags: string[];
  /** `mode=undispatched` only. */
  approved_at?: string | null;
  scheduled_at?: string | null;
  external_ref?: string | null;
  dispatch_failure?: DispatchFailureSummary | null;
}

export interface ReviewQueueResponse {
  items: ReviewQueueRow[];
  next_cursor: string | null;
}

/** One evaluator run, from the route's own model. The hand-written version of this was MORE
 *  precise than the route about `fix_list`'s items, so those keys moved INTO the route as
 *  `FixListEntry` rather than being dropped on the way to the schema. */
export type ReviewEvaluatorResult = components["schemas"]["EvaluatorHistoryEntry"];

/** One audit row, from the route's own model. `id` is a bigserial, so a NUMBER on the wire —
 *  this was hand-written as `string` until 2026-10-29, and the fixture that writes `id: 1` was
 *  the thing that had it right. */
export type ReviewAuditRow = components["schemas"]["AuditTrailRow"];

/** One review item, from the detail route's own model (`DetailResponse.item`).
 *
 *  The generated type is the stricter one in four places the hand-written version had wrong:
 *  `author_display_name`, `published_version` and `target_platforms` are never null on the
 *  wire, and `embedded_creations` carries `StoredEmbeddedCreation` rather than `unknown`. */
export type ReviewItemDetail = components["schemas"]["ReviewItemOut"];

/** One structural problem the body will produce when rendered, from the route's own
 *  model. Severity is earned, not asserted: `breaks` means the shipped renderer visibly
 *  produces the wrong output. */
export type IntegrityFinding = components["schemas"]["IntegrityFindingOut"];

/** `GET /review/items/{id}`, from the route's own models (declared 2026-10-29).
 *
 * It was hand-written here until the route's five `dict` fields were declared — swapping before
 * that turned 12 real reads into `never`, because the generated shape was `Record<string, never>`
 * for everything this page renders. */
export type ReviewDetailResponse = components["schemas"]["DetailResponse"];

// ─── Batch review (content-audit-queue) ───────────────────────────────────

/** Everything a review view needs to render one surface, from the route's own model.
 *  `limit` is ALWAYS the body's ceiling; a field with its own ceiling declares it
 *  separately (`title_limit`), so one counter never points at two fields. */
export type SurfaceMeta = components["schemas"]["SurfaceMetaOut"];

export type SurfaceCatalogue = components["schemas"]["SurfaceCatalogueOut"];

/** One row as the compare board reads it, from the route's own model.
 *  `blocked` and `bulk_eligible` are SERVER-computed and re-checked by every action: the
 *  client uses them to grey out a checkbox, and a stale tab must not be able to approve
 *  something a human was supposed to read. */
export type BatchItem = components["schemas"]["BatchItemOut"];

export type BatchSummary = components["schemas"]["BatchSummaryOut"];

export type BatchListResponse = components["schemas"]["BatchListOut"];

export type SharedThread = components["schemas"]["SharedThreadOut"];

export type IssueClusterDto = components["schemas"]["IssueClusterOut"];

export type BatchSegment = components["schemas"]["BatchSegmentOut"];

export type BatchDetailResponse = components["schemas"]["BatchDetailOut"];

/** One logged decision for the decisions rail, from the route's own model.
 *  `via` is absent on a single-item decision — the UI shows "one-by-one" rather than
 *  inventing a gesture that did not happen. */
export type DecisionRow = components["schemas"]["DecisionRowOut"];

export type BulkVia = "list" | "batch" | "compare" | "issue";

/** What one bulk gesture did, from the route's own model (declared 2026-10-30).
 *
 *  `skipped` NAMES every item the server held out, with the gate's own sentence — a bulk call
 *  that quietly drops three of nine is the failure the shape exists to prevent, and `requested`
 *  is what the client asked for so the board can say "7 of 9" without recomputing it. */
export type BulkResult = components["schemas"]["BulkResult"];

export const review = {
  queue: (params?: {
    mode?: ReviewMode;
    status?: ContentStatus;
    risk_tier?: RiskTier;
    surface?: string;
    section?: string;
    flag?: string;
    cursor?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.mode) qs.set("mode", params.mode);
    if (params?.status) qs.set("status", params.status);
    if (params?.risk_tier) qs.set("risk_tier", params.risk_tier);
    if (params?.surface) qs.set("surface", params.surface);
    if (params?.section) qs.set("section", params.section);
    if (params?.flag) qs.set("flag", params.flag);
    if (params?.cursor) qs.set("cursor", params.cursor);
    if (params?.limit) qs.set("limit", String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<ReviewQueueResponse>(`/review/queue${suffix}`);
  },

  get: (id: string) =>
    request<ReviewDetailResponse>(`/review/items/${id}`),

  /** Would dispatch accept this row as it stands? Read-only — no audit
   *  row, no event. Same chain as the approve gate and the dispatch
   *  route, so what this says before Approve is what happens after. */
  preflight: (id: string) =>
    request<DispatchPreflight>(`/review/items/${id}/preflight`),

  // ── Batch review (content-audit-queue) ────────────────────────────────

  /** The surface catalogue, derived server-side from the same vocabulary
   *  the DB CHECK enforces. Never hand-maintain a second copy — the last
   *  one drifted and two filters silently matched nothing. */
  surfaces: () => request<components["schemas"]["SurfaceCatalogueOut"]>("/review/surfaces"),

  batches: () => request<BatchListResponse>("/review/batches"),

  /** The decision log. A bulk gesture appears as N rows, never one — the
   *  `via` stamp is what makes nine approvals in one second answerable. */
  decisions: (limit = 100) =>
    request<components["schemas"]["DecisionsOut"]>(`/review/decisions?limit=${limit}`),

  batch: (sourceId: string) =>
    request<BatchDetailResponse>(`/review/batches/${sourceId}`),

  /** Bulk decisions. One gesture, one logged decision per item — the
   *  response names every item that was skipped and why, because an item
   *  that vanishes from the count with no explanation reads as a bug. */
  bulkApprove: (args: {
    item_ids: string[];
    via: BulkVia;
    source_id?: string;
    issue_id?: string;
    notes?: string;
  }) =>
    request<BulkResult>("/review/bulk/approve", {
      method: "POST",
      body: JSON.stringify(args),
    }),

  bulkReject: (args: {
    item_ids: string[];
    via: BulkVia;
    source_id?: string;
    issue_id?: string;
    reason: string;
  }) =>
    request<BulkResult>("/review/bulk/reject", {
      method: "POST",
      body: JSON.stringify(args),
    }),

  bulkSendBack: (args: {
    item_ids: string[];
    via: BulkVia;
    source_id?: string;
    issue_id?: string;
    feedback_text: string;
    dimension?: SendBackDimension;
  }) =>
    request<BulkResult>("/review/bulk/send_back", {
      method: "POST",
      body: JSON.stringify(args),
    }),

  approve: (id: string, notes?: string) =>
    request<components["schemas"]["ItemDecided"]>(
      `/review/items/${id}/approve`,
      { method: "POST", body: JSON.stringify({ notes: notes ?? null }) },
    ),

  // G4: allow-listed reviewer edits on a drafted / in_review row. The
  // backend restricts field_patches to: title, subtitle, meta_title,
  // meta_description, topic_tags, pillar_slug, slug, draft_body.
  // A patch that includes draft_body writes a `body_edit` audit row;
  // meta-only patches write `manual_override`. `reason` is required.
  edit: (id: string, field_patches: Record<string, unknown>, reason: string) =>
    request<components["schemas"]["ItemEdited"]>(
      `/review/items/${id}/edit`,
      { method: "POST", body: JSON.stringify({ field_patches, reason }) },
    ),

  reject: (id: string, reason: string) =>
    request<components["schemas"]["ItemDecided"]>(
      `/review/items/${id}/reject`,
      { method: "POST", body: JSON.stringify({ reason }) },
    ),

  sendBackToWriter: (id: string, feedback_text: string, dimension: SendBackDimension = "operator_general") =>
    request<components["schemas"]["SentBackToWriter"]>(
      `/review/items/${id}/send_back_to_writer`,
      { method: "POST", body: JSON.stringify({ feedback_text, dimension }) },
    ),

  uploadImage: (
    id: string,
    file: File,
    variant: string = "hero",
    alreadyCanonical = false,
  ) => {
    const form = new FormData();
    form.append("file", file);
    // alreadyCanonical: the Reframe modal already cropped to the exact
    // target aspect (per surface), so tell the server to skip its 21:9
    // re-crop and store the bytes as-is.
    const ac = alreadyCanonical ? "&already_canonical=true" : "";
    return requestMultipart<{
      ok: boolean;
      id: string;
      variant: string;
      url: string;
      photoreal_score: number;
      /** Face-aware focal point for the Reframe modal; null when no
       *  face detected. Only populated for variant=hero. */
      focal_point?: { x: number; y: number } | null;
    }>(`/review/items/${id}/upload_image?variant=${encodeURIComponent(variant)}${ac}`, form);
  },

  // Hero auto-sourcing. Same two routes blogAdmin.fetchHero /
  // blogAdmin.selectHero call — they are review-tier routes, and the
  // review dashboard is where the evaluator feedback that should shape
  // the hero actually lives.
  fetchHero: (id: string, payload: { feedback_text?: string } = {}) =>
    request<FetchHeroResponse>(`/review/items/${id}/fetch_hero`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  selectHero: (id: string, payload: HeroSelectRequest) =>
    request<components["schemas"]["HeroSelected"]>(
      `/review/items/${id}/select_hero`,
      { method: "POST", body: JSON.stringify(payload) },
    ),

  requestAtomization: (id: string) =>
    request<components["schemas"]["AtomizationRequested"]>(
      `/review/items/${id}/request_atomization`,
      { method: "POST", body: JSON.stringify({}) },
    ),

  // social-composer-ui S2 — grouped child rows for a source. Backend
  // queries by generation_params_jsonb.source_content_item_id.
  listAtomizations: (sourceId: string) =>
    request<AtomizationsResponse>(`/review/items/${sourceId}/atomizations`),
};

/** One atomized child row, from the route's own model. `thread_position` is an open dict
 *  because the atomizer stores whatever an authoring model emitted — validate it there
 *  before narrowing this. */
export type AtomizationChildRow = components["schemas"]["AtomizationChildOut"];

export type AtomizationsResponse = components["schemas"]["AtomizationsOut"];

// The old link-in-bio callout API (listLinkInBioPending +
// markLinkInBioUpdated) was retired 2026-07-16 when bio was pinned to
// worldby.me statically per social-post-cta-strategy R3. Backend
// endpoints remain for provenance but no frontend consumer today.

export const publishingApi = {
  dispatch: (contentItemId: string, scheduledAt: string | null) =>
    request<{ ok: boolean; external_ref: string; scheduler_ref: string }>(
      "/publishing/dispatch",
      {
        method: "POST",
        body: JSON.stringify({ content_item_id: contentItemId, scheduled_at: scheduledAt }),
      },
    ),

  cancel: (contentItemId: string) =>
    request<{ ok: boolean; scheduler_ref: string; reverted_to_approved: boolean }>(
      `/publishing/cancel/${contentItemId}`,
      { method: "POST" },
    ),
};

// social-composer-ui S9 — per-child performance read for the strip.
export interface PerformanceRow {
  source: string;
  metric_window: string;
  window_start: string | null;
  window_end: string | null;
  metrics: Record<string, number>;
  updated_at: string | null;
}

export const contentPerformanceApi = {
  read: (itemId: string, metricWindow: "24h" | "7d" | "28d" | "30d" | "90d" | "lifetime" = "24h") =>
    request<{ item_id: string; metric_window: string; rows: PerformanceRow[] }>(
      `/content/performance/${itemId}?metric_window=${metricWindow}`,
    ),
};

export const REVIEW_IMAGE_VARIANTS = [
  "hero",
  "social_pinterest",
  "social_instagram_feed",
  "social_instagram_carousel",
  "social_instagram_story",
  "social_facebook_page",
  "social_x",
  "social_linkedin",
] as const;
export type ReviewImageVariant = (typeof REVIEW_IMAGE_VARIANTS)[number];

export const REVIEW_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const REVIEW_IMAGE_ACCEPT = "image/png,image/jpeg,image/jpg,image/webp";

// Admin
export const admin = {
  setFriendingToggle: (enabled: boolean) =>
    request<{ enabled: boolean }>("/admin/settings/friending-enabled", {
      method: "PUT",
      body: JSON.stringify({ enabled }),
    }),

  getPublicProjectsAdmin: (statusFilter?: string) =>
    request<{ id: string; title: string; creator_username?: string; sharing_scope: string; shared_at?: string; moderation_status: string; classifications: string[] }[]>(
      `/admin/projects/public${statusFilter ? `?status_filter=${statusFilter}` : ""}`
    ),

  setModerationStatus: (projectId: string, status: "visible" | "curated" | "suppressed") =>
    request<{ status: string }>(`/admin/projects/${projectId}/moderation`, {
      method: "PUT",
      body: JSON.stringify({ status }),
    }),

  previewProject: (projectId: string) =>
    request<{ id: string; title: string; project_type: string; s3_html_key?: string }>(
      `/admin/projects/${projectId}/preview`
    ),

  listUsers: (q?: string) =>
    request<AdminUser[]>(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`),

  setUserAdmin: (userId: string, isAdmin: boolean) =>
    request<AdminUser>(`/admin/users/${userId}/admin`, {
      method: "PUT",
      body: JSON.stringify({ is_admin: isAdmin }),
    }),

  deleteUser: (userId: string) =>
    request<{ deleted: string }>(`/admin/users/${userId}`, { method: "DELETE" }),
};

/** One row of the admin user list, from the route's own model, as the wire carries it.
 *  `email`, `username`, `created_at` and `parent_email` are nullable and always present —
 *  see `Wire`. */
export type AdminUser = Wire<components["schemas"]["AdminUserResponse"]>;

// ── Content scheduling calendar (pipeline-scheduling-and-cadence A1) ──
// Backs /admin/calendar (Surface A) + the review card's schedule strip
// (Surface B). Cap and posting windows arrive as SERVER config — an
// operator retuning X's cap must not need a frontend deploy.

/** The three scheduling lanes, taken from the calendar row's own model so this cannot drift
 *  from `schedule_queue.VALID_PRIORITIES`. */
export type SchedulingPriority = CalendarItem["priority"];

/** One posting window: which weekdays (0=Mon..6=Sun), which local spans (`times` is
 *  `'HH:MM-HH:MM'` in the calendar response's `timezone`), from the route's own model. */
export type SchedulingWindow = components["schemas"]["SurfaceWindows"];

/** One surface's cadence limits, from `GET /scheduling/calendar`'s own model. */
export type SchedulingSurfaceConfig = components["schemas"]["SurfaceConfig"];

/** One scheduled row, from the route's own model (declared 2026-10-29).
 *
 * `status` and `priority` were `str` on the route while this declared unions, which is why the
 * swap waited. `priority`'s union is safe on BOTH sides for the same reason: `priority_of()`
 * degrades an unrecognised jsonb value to `normal`, so the three lanes are all the wire can
 * carry. */
export type CalendarItem = components["schemas"]["CalendarItem"];

/** The latest dispatch refusal on a scheduled row, from the route's own model.
 *  `reason` is a code (`body_invalid`, `reddit_subreddit_required`, …); `detail` is the
 *  provider's or our validator's text, truncated at 300. */
export type CalendarDispatchFailure = Wire<components["schemas"]["DispatchFailure"]>;

/** How full one surface's one local day is (`day` is a local ISO date in the response's
 *  `timezone`), from the route's own model. */
export type CalendarDayCapacity = components["schemas"]["DayCapacity"];

/** A registry entry's next fires inside the calendar window, from the route's own model. */
export type CalendarRegistryFire = components["schemas"]["RegistryFireInWindow"];

export type CalendarResponse = components["schemas"]["CalendarResponse"];

/** One line of `GET /scheduling/preview/{id}`'s explanation, from the route's own model. */
export type SchedulePreviewLine = components["schemas"]["ExplanationLine"];

export type SchedulePreviewResponse = components["schemas"]["PreviewResponse"];

/** One row the reschedule moved — the primary or a displaced one — from the route's own
 *  model. */
export type ScheduleMove = components["schemas"]["MoveOut"];

export type RescheduleResponse = components["schemas"]["RescheduleResponse"];

export interface RescheduleRequest {
  content_item_id: string;
  mode?: "auto" | "exact";
  scheduled_at?: string;
  priority?: SchedulingPriority;
  displace?: boolean;
  override_reason?: string;
  /** Compute + report, then roll back. Powers the drop-confirm dialog. */
  dry_run?: boolean;
}

export const scheduling = {
  calendar: (params: {
    from: string;
    to: string;
    surface_filter?: string[];
    pillar_filter?: string[];
  }) => {
    const qs = new URLSearchParams();
    qs.set("from", params.from);
    qs.set("to", params.to);
    (params.surface_filter ?? []).forEach((s) => qs.append("surface_filter", s));
    (params.pillar_filter ?? []).forEach((p) => qs.append("pillar_filter", p));
    return request<CalendarResponse>(`/scheduling/calendar?${qs.toString()}`);
  },

  preview: (contentItemId: string) =>
    request<SchedulePreviewResponse>(`/scheduling/preview/${contentItemId}`),

  reschedule: (body: RescheduleRequest) =>
    request<RescheduleResponse>("/scheduling/reschedule", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Pull a row off the calendar, back to the review queue. */
  unschedule: (contentItemId: string) =>
    request<{ content_item_id: string; was_at: string | null; status: string }>(
      `/scheduling/unschedule/${contentItemId}`,
      { method: "POST" },
    ),
};
